import React, { useRef, ComponentProps, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus, vs } from 'react-syntax-highlighter/dist/esm/styles/prism';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { FileText } from 'lucide-react';
import { useThemeController } from '@/hooks/useThemeController';
import { Button } from '@/components/ui/button';
import PdfPreviewModal from '@/components/PdfPreviewModal';
import './MarkdownRenderer.css';

/**
 * Enhanced Markdown Renderer Component
 * 
 * Features:
 * - GitHub Flavored Markdown support (tables, strikethrough, etc.)
 * - HTML rendering support
 * - Syntax highlighting for code blocks
 * - Interactive PDF buttons for div.popup_pdf containers
 * - Theme-aware styling (light/dark mode)
 * - Responsive design
 * - Customizable PDF button click handlers
 * 
 * Usage:
 * ```tsx
 * <MarkdownRenderer 
 *   content="# Hello World\nThis is **markdown** content"
 *   onPdfButtonClick={(data) => console.log('PDF clicked:', data)}
 * />
 * ```
 * 
 * PDF Button Feature:
 * Any div with class "popup_pdf" will have its child divs automatically
 * converted to clickable buttons with hover effects and click logging.
 */

export interface MarkdownRendererProps {
  content: string;
  className?: string;
  onPdfButtonClick?: (buttonData: PdfButtonData) => void;
}

export interface PdfButtonData {
  textContent: string;
  className: string;
  timestamp: string;
  parsedData: Record<string, string>;
}

/**
 * Parses className patterns like "body|<KEY>|<VALUE>" and extracts key-value pairs
 */
const parseClassNameData = (className: string): Record<string, string> => {
  const parsedData: Record<string, string> = {};
  
  // Split className by spaces to get individual classes
  const classes = className.split(' ');
  
  classes.forEach(classItem => {
    // Look for pattern "body|<KEY>|<VALUE>"
    const match = classItem.match(/^body\|([^|]+)\|(.+)$/);
    if (match) {
      const [, key, value] = match;
      parsedData[key] = value;
      console.log(`📋 Parsed: ${key} = ${value}`);
    }
  });
  
  console.log("🚀 ~ parseClassNameData ~ parsedData:", parsedData)
  return parsedData;
};

type CodeProps = ComponentProps<'code'> & { children: React.ReactNode };
type DivProps = ComponentProps<'div'> & { children: React.ReactNode };
type ImgProps = ComponentProps<'img'>;
type LinkProps = ComponentProps<'a'> & { children: React.ReactNode };

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({
  content,
  className = '',
  onPdfButtonClick
}) => {
  const { resolvedTheme } = useThemeController();
  const isDark = resolvedTheme === 'dark';
  const containerRef = useRef<HTMLDivElement>(null);
  const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);
  const [selectedPdfData, setSelectedPdfData] = useState<PdfButtonData | null>(null);

  const handleContainerClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    
    if (target.classList.contains('clickable-pdf-div')) {
      e.preventDefault();
      e.stopPropagation();
      
      const buttonData: PdfButtonData = {
        textContent: target.textContent || '',
        className: target.className,
        timestamp: new Date().toISOString(),
        parsedData: {
          ...parseClassNameData(target.className)
        }
      };
      
      // Set the selected PDF data and open modal
      setSelectedPdfData(buttonData);
      setIsPdfModalOpen(true);
      
      // Call the optional callback if provided
      if (onPdfButtonClick) {
        onPdfButtonClick(buttonData);
      }
      
      // Default console log for debugging
      console.log('PDF Button clicked:', buttonData);
    }
  };

  const handleShowPdfCitation = () => {
    setIsPdfModalOpen(true);
  };

  const handleClosePdfModal = () => {
    setIsPdfModalOpen(false);
    setSelectedPdfData(null);
  };

  const renderCodeBlock = ({ className, children }: CodeProps) => {
    const match = /language-(\w+)/.exec(className || '');
    const isInline = !match;
    
    return !isInline ? (
      <SyntaxHighlighter
        style={isDark ? vscDarkPlus : vs}
        language={match[1]}
        PreTag="div"
      >
        {String(children).replace(/\n$/, '')}
      </SyntaxHighlighter>
    ) : (
      <code className={className}>
        {children}
      </code>
    );
  };

  const renderDiv = ({ children, className, ...props }: DivProps) => {
    const isPopupPdf = className?.includes('popup_pdf');
    
    if (isPopupPdf) {
      return (
        <div className={`markdown-div ${className || ''}`} {...props}>
          {children}
        </div>
      );
    }
    
    // Add clickable class to divs that have content (likely PDF buttons)
    const hasContent = typeof children === 'string' && children.trim().length > 0;
    const shouldBeClickable = hasContent && !className?.includes('markdown-div');
    
    if (shouldBeClickable) {
      return (
        <div 
          className={`markdown-div clickable-pdf-div ${className || ''}`}
          style={{ cursor: 'pointer', userSelect: 'none' }}
          {...props}
        >
          {children}
        </div>
      );
    }
    
    return (
      <div className={`markdown-div ${className || ''}`} {...props}>
        {children}
      </div>
    );
  };

  const renderImage = ({ src, alt, ...props }: ImgProps) => (
    <img 
      src={src} 
      alt={alt} 
      className="markdown-img"
      loading="lazy"
      {...props}
    />
  );

  const renderLink = ({ children, href }: LinkProps) => (
    <a className="markdown-link" href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );

  return (
    <>
      <div 
        ref={containerRef} 
        className={`markdown-renderer ${className}`}
        onClick={handleContainerClick}
      >
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeRaw]}
          components={{
            code: renderCodeBlock,
            h1: ({ children }) => <h1 className="markdown-h1">{children}</h1>,
            h2: ({ children }) => <h2 className="markdown-h2">{children}</h2>,
            h3: ({ children }) => <h3 className="markdown-h3">{children}</h3>,
            h4: ({ children }) => <h4 className="markdown-h4">{children}</h4>,
            h5: ({ children }) => <h5 className="markdown-h5">{children}</h5>,
            h6: ({ children }) => <h6 className="markdown-h6">{children}</h6>,
            p: ({ children }) => <p className="markdown-p">{children}</p>,
            ul: ({ children }) => <ul className="markdown-ul">{children}</ul>,
            ol: ({ children }) => <ol className="markdown-ol">{children}</ol>,
            li: ({ children }) => <li className="markdown-li">{children}</li>,
            blockquote: ({ children }) => <blockquote className="markdown-blockquote">{children}</blockquote>,
            table: ({ children }) => <table className="markdown-table">{children}</table>,
            thead: ({ children }) => <thead className="markdown-thead">{children}</thead>,
            tbody: ({ children }) => <tbody className="markdown-tbody">{children}</tbody>,
            tr: ({ children }) => <tr className="markdown-tr">{children}</tr>,
            th: ({ children }) => <th className="markdown-th">{children}</th>,
            td: ({ children }) => <td className="markdown-td">{children}</td>,
            a: renderLink,
            strong: ({ children }) => <strong className="markdown-strong">{children}</strong>,
            em: ({ children }) => <em className="markdown-em">{children}</em>,
            div: renderDiv,
            span: ({ children, className, ...props }) => (
              <span className={`markdown-span ${className || ''}`} {...props}>
                {children}
              </span>
            ),
            img: renderImage,
          }}
        >
          {content}
        </ReactMarkdown>
      </div>

      <PdfPreviewModal
        isOpen={isPdfModalOpen}
        onClose={handleClosePdfModal}
        selectedPdfData={selectedPdfData}
      />
    </>
  );
};

export default MarkdownRenderer;
